import { STATUS_BAD_REQUEST, STATUS_OK } from "../../models/httpsStatus";
import { ISchedulesArray } from "../../models/interfaces/schedules";
import { AppDispatch } from "../../redux/index";
import {
  updateNotificationMessage,
  updateNotificationStatus,
} from "../../redux/states/notificationSlice";
import { editEmployeeSchedulesService } from "../../services/puts";
import { adaptScheduleConflictError } from "../adaptScheduleConflictError";
import { IScheduleRegistrationItem } from "../../services/getScheduleImpact.service";

/**
 * US2 spec 016 — Construye el payload `scheduleRegistration` a partir del estado
 * del modal de horarios. Extraido para poder reutilizarlo tanto en el guardado
 * como en el preview de impacto (paridad de datos enviados al backend).
 *
 * Lanza un Error si hay horarios marcados como disponibles pero incompletos.
 */
export const buildScheduleRegistration = (
  schedules: ISchedulesArray[]
): IScheduleRegistrationItem[] => {
  const filterSchedules = schedules.map((scheduleList) => {
    const temp = scheduleList.scheduleList.map((schedule) => {
      const noShedules = schedule.startTime === "" || schedule.endTime === "";
      const noIdSchedule = String(schedule.idSchedule).includes("static");
      let formattedStartTime;
      let formattedEndTime;

      if (schedule.isAvailable && noShedules) {
        throw new Error("Verifique que todos los horarios estén completos");
      } else if (noIdSchedule && noShedules) {
        return undefined;
      }

      if (schedule.startTime.includes("AM")) {
        formattedStartTime = schedule.startTime.replace(" AM", "");
      } else {
        const startTime = schedule.startTime.replace(" PM", "").split(":");
        if (Number(startTime[0]) !== 12) {
          formattedStartTime = `${Number(startTime[0]) + 12}:${startTime[1]}`;
        } else {
          formattedStartTime = `${Number(startTime[0])}:${startTime[1]}`;
        }
      }

      if (schedule.endTime.includes("AM")) {
        formattedEndTime = schedule.endTime.replace(" AM", "");
      } else {
        const endTime = schedule.endTime.replace(" PM", "").split(":");
        if (Number(endTime[0]) !== 12) {
          formattedEndTime = `${Number(endTime[0]) + 12}:${endTime[1]}`;
        } else {
          formattedEndTime = `${Number(endTime[0])}:${endTime[1]}`;
        }
      }

      return {
        id: !String(schedule.idSchedule).includes("static")
          ? schedule.idSchedule
          : null,
        dayOfTheWeek: scheduleList.idDay + 1,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        estado: schedule.isAvailable ? 1 : 0,
      };
    });
    return temp;
  });

  const separatedSchedules = filterSchedules
    .flatMap((dia) =>
      dia.map((horario) => {
        if (!horario) {
          return undefined;
        }
        return {
          ...horario,
          dayOfTheWeek: horario.dayOfTheWeek,
        };
      })
    )
    .filter((schedule) => schedule !== undefined)
    .filter((schedule) => {
      // Filtrar horarios con tiempo inválido (e.g. "12:undefined")
      if (
        schedule.startTime?.includes("undefined") ||
        schedule.endTime?.includes("undefined")
      ) {
        return false;
      }
      return true;
    });

  return separatedSchedules as IScheduleRegistrationItem[];
};

export const updateEmployeeSchedulesAdapter = async (
  dispatch: AppDispatch,
  idEmployee: number,
  schedules: ISchedulesArray[],
  onSaveSuccess?: () => void,
  reassignAffected?: boolean
) => {
  try {
    const separatedSchedules = buildScheduleRegistration(schedules);

    const payload = {
      idEmployee: idEmployee,
      scheduleRegistration: separatedSchedules,
      ...(reassignAffected !== undefined ? { reassignAffected } : {}),
    };

    const response: IResponse = await editEmployeeSchedulesService(payload);

    if (response.status !== STATUS_OK) {
      const conflictMessage = adaptScheduleConflictError(response);
      dispatch(
        updateNotificationMessage(
          conflictMessage || "Error al actualizar los horarios."
        )
      );
      dispatch(updateNotificationStatus(400));
      return;
    }

    if (onSaveSuccess) {
      onSaveSuccess();
    } else {
      dispatch(updateNotificationMessage("Aguarde un momento..."));
      dispatch(updateNotificationStatus(200));

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  } catch (error) {
    dispatch(
      updateNotificationMessage(
        "Error al actualizar los horarios. " + error.message
      )
    );
    dispatch(updateNotificationStatus(STATUS_BAD_REQUEST));
    return;
  }
};
